import { defer, EMPTY, expand, forkJoin, from, map, Observable, reduce } from "rxjs"
import { BybitRestClient } from "../../../market"
import { Deal, Funding, Order, OrderSide, OrderType, Position, PositionSide } from "../../../model/trade"
import { AccountOrderV5, GetAccountOrdersParamsV5, GetExecutionListParamsV5, PositionV5 } from "libs/bybit-api/src"
import { parseFloatSafe, parseIntSafe } from "src/app/core/func"
import { DealsRequestParams, HistoryDataWithDeals } from "../model"

export class BybitHistoryProvider {

  constructor(private rest: BybitRestClient) {}

  requestPositions() {
    return forkJoin([
      this.innerRequestPositions('USDT'),
      this.innerRequestPositions('USDC'),
    ]).pipe(
      map(data => data.flat())
    )
  }

  private innerRequestPositions(settleCoin: string) {
    const params: GetAccountOrdersParamsV5 = {
      category: 'linear',
      settleCoin,
      limit: 200,
    }
    const req = defer(() => this.rest.getPositionInfo(params))
    return req.pipe(
      expand(data => {
        if (data.result.nextPageCursor && params.limit && data.result.list.length >= params.limit) {
          params.cursor = data.result.nextPageCursor
          return req
        }
        return EMPTY
      }),
      map(data => data.result.list),
      reduce((acc, list) => {
        acc.push(...list)
        return acc
      }, [] as PositionV5[]),
      map(data => data.map(row => {
        const position: Position = {
          createdTimestamp: parseIntSafe(row.createdTime),
          updateTimestamp: parseIntSafe(row.updatedTime),
          market: 'bybit',
          board: 'linear',
          symbol: row.symbol,
          entryPrice: row.avgPrice,
          liquidationPrice: row.liqPrice,
          size: row.side == 'Buy' ? row.size : -Math.abs(parseFloatSafe(row.size)),
          positionSide: row.positionIdx == 0 ? PositionSide.both : row.positionIdx == 1 ? PositionSide.long : row.positionIdx == 2 ? PositionSide.short : undefined,
          positionVolume: row.positionValue,
          unrealisedPnl: row.unrealisedPnl,
        }
        return position
      }).filter(position => parseFloatSafe(position.size) != 0))
    )
  }

  requestDeals(params: DealsRequestParams): Observable<HistoryDataWithDeals> {
    return from(this.rest.getExecutionList(this.prepareDealsRequestParams(params))).pipe(
      map(data => {
        const deals: Deal[] = []
        const fundings: Funding[] = []
        data.result.list.forEach(row => {
          if (row.execType == 'Funding') {
            const funding: Funding = {
              timestamp: parseIntSafe(row.execTime),
              market: 'bybit',
              board: 'linear',
              symbol: row.symbol,
              fee: row.execFee,
            }
            fundings.push(funding)
          }
          else {
            const deal: Deal = {
              timestamp: parseIntSafe(row.execTime),
              market: 'bybit',
              board: 'linear',
              symbol: row.symbol,
              price: row.execPrice,
              quantity: row.execQty,
              volume: parseFloatSafe(row.execPrice) * parseFloatSafe(row.execQty),
              fee: row.execFee,
              side: row.side == 'Buy' ? OrderSide.buy : OrderSide.sell,
              dealId: row.execId,
              orderId: row.orderId,
              clientOrderId: row.orderLinkId,
            }
            deals.push(deal)
          }
        })
        return {deals, fundings}
      }),
    )
  }

  requestOrders() {
    return from(this.rest.getHistoricOrders({category: 'linear', limit: 50})).pipe(
      map(data => data.result.list.map(row => this.fillOrder(row))),
    )
  }

  requestActiveOrders() {
    return forkJoin([
      this.innerRequestActiveOrders('USDT'),
      this.innerRequestActiveOrders('USDC'),
    ]).pipe(
      map(data => data.flat())
    )
  }

  private innerRequestActiveOrders(settleCoin: string) {
    const params: GetAccountOrdersParamsV5 = {
      category: 'linear',
      settleCoin,
      limit: 50,
    }
    const req = defer(() => this.rest.getActiveOrders(params))
    return req.pipe(
      expand(data => {
        if (data.result.nextPageCursor && params.limit && data.result.list.length >= params.limit) {
          params.cursor = data.result.nextPageCursor
          return req
        }
        return EMPTY
      }),
      map(data => data.result.list),
      reduce((acc, list) => {
        acc.push(...list)
        return acc
      }, [] as AccountOrderV5[]),
      map(data => data.map(row => this.fillOrder(row)))
    )
  }

  private fillOrder(row: AccountOrderV5) {
    let remainingQuantity = row.leavesQty
    let remainingVolume = parseFloatSafe(row.price) * parseFloatSafe(row.leavesQty)
    if (row.cumExecQty == '0' && row.leavesQty == '0') {
      remainingQuantity = row.qty
      remainingVolume = parseFloatSafe(row.price) * parseFloatSafe(row.qty)
    }
    const order: Order = {
      createdTimestamp: parseIntSafe(row.createdTime),
      updateTimestamp: parseIntSafe(row.updatedTime),
      type: row.orderType == 'Limit' ? OrderType.limit : (row.orderType == 'Market' ? OrderType.market : OrderType.unknown),
      status: row.orderStatus,
      market: 'bybit',
      board: 'linear',
      symbol: row.symbol,
      price: row.price,
      quantity: row.qty,
      volume: parseFloatSafe(row.price) * parseFloatSafe(row.qty),
      remainingQuantity: remainingQuantity,
      remainingVolume: remainingVolume,
      side: row.side == 'Buy' ? OrderSide.buy : OrderSide.sell,
      positionSide: row.positionIdx == 1 ? PositionSide.long : row.positionIdx == 2 ? PositionSide.short : PositionSide.both,
      orderId: row.orderId,
      clientOrderId: row.orderLinkId,
      isStop: row.stopOrderType.includes('Stop'),
      isClosePosition: row.reduceOnly,
    }
    if (order.isStop) {
      order.price = row.triggerPrice
      order.volume = parseFloatSafe(order.price) * parseFloatSafe(order.quantity)
      order.remainingQuantity = order.quantity
      order.remainingVolume = order.volume
    }
    return order
  }

  private prepareDealsRequestParams(params: DealsRequestParams) {
    const params2: GetExecutionListParamsV5 = {
      category: 'linear'
    }
    if (params.symbol) {
      params2.symbol = params.symbol
    }
    params2.limit = params.limit ?? 100
    return params2
  }

}
